import { action, computed, observable } from "mobx";
import { injectable } from "inversify";
import type { TurnstileInstance } from "@marsidev/react-turnstile";
import LocalizationService from "./localizationService";
import EventBus, { turnstileTokenReceived, turnstileErrorReceived, turnstileReset } from "./eventBus";

export interface ITurnstileService {
  readonly isInitialized: boolean;
  readonly token: string | undefined;
  readonly isValid: boolean;
  readonly info: string | undefined;
  readonly error: string | undefined;
  readonly instance: TurnstileInstance | undefined;

  initialize(context: string): void;
  reset(): void;
  setToken(token: string): void;
  setError(error: string): void;
  setInfo(info: string): void;
  setInstance(instance: TurnstileInstance): void;
  translateTurnstile(key: string): string;
}

export default class TurnstileService {
  @observable private _token?: string;
  @observable private _error?: string;
  @observable private _info?: string;
  @observable private _instance?: TurnstileInstance;
  @observable private _context?: string;
  @observable private _isInitialized = false;
  private unsubscriptions: Array<() => void> = [];

  constructor(private localizationService: LocalizationService) {}

  @computed
  get isInitialized(): boolean {
    return this._isInitialized && !!this._instance;
  }

  @computed
  get token(): string | undefined {
    return this._token;
  }

  @computed
  get isValid(): boolean {
    return !!this._token && !this._error;
  }

  @computed
  get info(): string | undefined {
    return this._info;
  }

  @computed
  get error(): string | undefined {
    return this._error;
  }

  @computed
  get instance(): TurnstileInstance | undefined {
    return this._instance;
  }

  @action.bound
  initialize(context: string): void {
    if (this._context !== context) {
      this._context = context;
      // Pouze reset stavu, ne instance (ta ještě neexistuje)
      this._token = undefined;
      this._error = undefined;
      this._info = undefined;
    }
    this._isInitialized = true;
  }

  @action.bound
  reset(): void {
    this._token = undefined;
    this._error = undefined;
    this._info = undefined;

    try {
      if (this._instance) {
        this._instance.reset();
      }
    } catch (error) {
      console.warn("Failed to reset Turnstile widget", error);
    }
  }

  @action.bound
  setToken = (token: string): void => {
    this._token = token;
    this._error = undefined;

    this.setInfo(this.translateTurnstile("success"));
  };

  @action.bound
  setError = (error?: string): void => {
    this._error = error ?? undefined;
    this._token = undefined;
  };

  @action.bound
  setInfo = (info: string): void => {
    this._info = info;
  };

  @action.bound
  resetToken = (): void => {
    this._token = undefined;
  };

  @action.bound
  setInstance = (instance: TurnstileInstance): void => {
    this._instance = instance;
  };

  translateTurnstile(key: string): string {
    return this.localizationService.translateGeneral(`turnstile.${key}`);
  }
}
